/**
 * Spline bridge helpers — DXF SPLINE <-> drawing-model SplineEntity.
 *
 * Lives in its own module to keep dxfBridge.ts under the 2000-line
 * ceiling (CLAUDE.md). Mirrors the splitting pattern established by
 * `dxfBridge.ellipse.ts`.
 *
 * Public surface:
 *   - drawingSplineToDxfSpline(entity, common, base): DxfSplineEntity
 *   - dxfSplineToDrawingSpline(dxf, base): SplineEntity
 *
 * dxfBridge.ts imports both helpers and dispatches them inside its
 * top-level `toDxfEntity` / `toDrawingEntity` switches.
 */
import type { DxfSplineEntity } from "@dxf-ai-editor/dxf-io";

import type {
  DrawingEntityBase,
  DrawingPoint3D,
  SplineEntity,
} from "./model";

const toPt3D = (p: { x: number; y: number; z?: number }): DrawingPoint3D => ({
  x: p.x,
  y: p.y,
  z: p.z ?? 0,
});

const toDxfPt = (p: DrawingPoint3D): { x: number; y: number; z: number } => ({
  x: p.x,
  y: p.y,
  z: p.z ?? 0,
});

/**
 * Convert a drawing-model SplineEntity into the DXF SPLINE wire shape.
 * Encodes the boolean flags into the DXF group-70 bitfield per the
 * AutoCAD spec (1=closed, 2=periodic, 4=rational, 8=planar, 16=linear).
 *
 * SL-15 — the SPLINE normal (DXF 210/220/230) is NOT written here: SPLINE is
 * a generic OCS-normal entity (NOT in the Block/Ellipse/Text/Hatch exclusion
 * list), so its non-default `entity.extrusion` is projected onto
 * `common.extrusion` by `projectCommonEntityProps` BEFORE this helper runs,
 * and the writer's `writeExtrusion(common)` emits it — exactly like
 * Line/Circle/Arc/LWPOLYLINE (S-EXTRUSION-STORE). `common` is passed through
 * unchanged; do not double-handle the extrusion here.
 *
 * SL-15 round-7 @codex F2 — that projection is PLANAR-GATED: a NONPLANAR spline
 * (`planar: false`, group-70 bit 8 clear) NEVER projects its extrusion onto
 * `common`, so it emits NO 210 even when an in-memory `SplineEntity` carries a
 * non-default `extrusion`. The gate lives in `projectCommonEntityProps` (the
 * single extrusion-projection site) — symmetric with the import gate in
 * `dxfSplineToDrawingSpline`. So by the time `common` reaches this helper it
 * already has NO extrusion for a nonplanar spline; a planar spline keeps it.
 */
export function drawingSplineToDxfSpline(
  entity: SplineEntity,
  common: DxfSplineEntity["common"],
  base: {
    kind: "supported-entity";
    subclassMarkers: string[];
    extraGroups: { code: number; value: string }[];
  },
): DxfSplineEntity {
  const flags =
    (entity.closed ? 1 : 0) |
    (entity.periodic ? 2 : 0) |
    (entity.rational ? 4 : 0) |
    (entity.planar ? 8 : 0) |
    (entity.linear ? 16 : 0);

  const geom: DxfSplineEntity["geometry"] = {
    degree: entity.degree,
    flags,
    closed: entity.closed,
    periodic: entity.periodic,
    rational: entity.rational,
    planar: entity.planar,
    linear: entity.linear ?? false,
    knots: [...entity.knots],
    controlPoints: entity.controlPoints.map(toDxfPt),
    weights: [...entity.weights],
    fitPoints: entity.fitPoints.map(toDxfPt),
    knotTolerance: entity.knotTolerance,
    controlPointTolerance: entity.controlPointTolerance,
    fitTolerance: entity.fitTolerance,
  };
  if (entity.startTangent) geom.startTangent = toDxfPt(entity.startTangent);
  if (entity.endTangent) geom.endTangent = toDxfPt(entity.endTangent);

  return {
    kind: base.kind,
    common,
    subclassMarkers: base.subclassMarkers,
    extraGroups: base.extraGroups,
    type: "SPLINE",
    geometry: geom,
  };
}

/**
 * Convert a DXF SPLINE entity into a drawing-model SplineEntity.
 *
 * SL-15 — the SPLINE normal (DXF 210/220/230) is parsed by dxf-io onto
 * `dxf.common.extrusion` (the generic COMMON_GROUP_READERS 210/220/230 path).
 * Carry a NON-default value onto the optional `extrusion` model field; an
 * absent or identity (0,0,1) normal maps to no field (absent ⇒ the WCS
 * default), mirroring `extrusionField` for Line/Circle/Arc.
 */
export function dxfSplineToDrawingSpline(
  dxf: DxfSplineEntity,
  base: DrawingEntityBase,
): SplineEntity {
  const g = dxf.geometry;
  const result: SplineEntity = {
    ...base,
    type: "Spline",
    degree: g.degree,
    closed: g.closed,
    periodic: g.periodic,
    rational: g.rational,
    planar: g.planar,
    fitPoints: g.fitPoints.map(toPt3D),
    controlPoints: g.controlPoints.map(toPt3D),
    knots: [...g.knots],
    weights: [...g.weights],
    knotTolerance: g.knotTolerance ?? 1e-7,
    controlPointTolerance: g.controlPointTolerance ?? 1e-10,
    fitTolerance: g.fitTolerance ?? 1e-10,
  };
  if (g.startTangent) result.startTangent = toPt3D(g.startTangent);
  if (g.endTangent) result.endTangent = toPt3D(g.endTangent);
  // DXF group 70 bit 16 (S-SPLINE) — only stored when set so the typed
  // model stays compact (absent ⇒ false).
  if (g.linear) result.linear = true;
  // SL-15 round-6 @codex F3 — DXF 210/220/230 normal, carried only when the
  // spline is PLANAR (group-70 bit 8) AND the normal is non-default. A
  // NONPLANAR spline has NO OCS plane, so the normal is semantically
  // meaningless (ezdxf `entities/spline.py`: `extrusion` is the plane normal
  // for a planar spline; Autodesk omits 210 when nonplanar). Storing it for a
  // nonplanar input would let the export channel (projectCommonEntityProps →
  // writeExtrusion) re-emit a 210 for a nonplanar spline, contradicting the
  // round-5 "nonplanar emits no 210" rule. Absent / (0,0,1) / nonplanar ⇒ no
  // field (the WCS default). Symmetric with the export channel that emits it.
  const ext = dxf.common.extrusion;
  if (g.planar && ext && !(ext.x === 0 && ext.y === 0 && ext.z === 1)) {
    result.extrusion = { x: ext.x, y: ext.y, z: ext.z };
  }
  return result;
}
